import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { PageHeader, PageShell } from '@/components/PageShell';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDebouncedSearchParam, useSetSearchParam } from '@/lib/searchParams';
import { useTableSort } from '@/lib/tableSort';
import {
    allBrandsState,
    allEquipmentTypesState,
    DOCUMENT_LIBRARY_LIMIT,
    documentIdsForSpellingState,
    modelDocumentLibraryState,
} from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { documentTitle } from '@rock/manuals';
import { buildSpellingKey } from '@rock/models';
import type { BrandId, EquipmentTypeId, ModelDocument } from '@rock/types';
import { ExternalLink } from 'lucide-react';
import { type FC, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CoveragePills, DocumentBrandChips, DocumentStateBadges, TagChips } from './documentShared';
import { brandLabel, DOCUMENT_FILTERS, type DocumentFilter, filterDocuments, modelNumberQuery } from './documentSearch';
import { brandsById } from './modelBrand';

/**
 * The document library: every `documents/{contentHash}` artifact, across models, with the brands and
 * types each is for. Models are the usual way in — but an artifact is shared, and which COVERAGE rules
 * exist is answerable nowhere else. A row opens the document's page, where its linked models are.
 *
 * Search reads titles, tags, brand names, type titles and rule predicates; a query shaped like a model
 * number also finds documents whose rule's prefixes or ranges match it and those linked to any model
 * under that number.
 */
export const DocumentsListPage: FC = () => {
    const documents = useAsyncAtomValue(modelDocumentLibraryState, []);
    const brands = useAsyncAtomValue(allBrandsState, []);
    const types = useAsyncAtomValue(allEquipmentTypesState, []);
    const brandLookup = useMemo(() => brandsById(brands), [brands]);
    const typeTitles = useMemo(() => new Map(types.map((type) => [type.id, type.title])), [types]);
    const [searchParams] = useSearchParams();
    const setSearchParam = useSetSearchParam();
    const { sort, setSort } = useTableSort();
    const filter: DocumentFilter = DOCUMENT_FILTERS.find((value) => value === searchParams.get('filter')) ?? 'all';
    const brandId = searchParams.get('brand') ?? undefined;
    const typeId = searchParams.get('type') ?? undefined;
    const query = searchParams.get('q') ?? '';
    const { input: searchInput, onChange: handleSearchChange } = useDebouncedSearchParam('q');

    const number = modelNumberQuery(query);
    const linkedByNumber = useAsyncAtomValue(
        useMemo(() => documentIdsForSpellingState(number ? buildSpellingKey(number) : undefined), [number]),
        [],
    );

    const visible = useMemo(
        () =>
            filterDocuments(documents, {
                filter,
                brandId,
                typeId,
                query,
                linkedByNumber: new Set(linkedByNumber),
                brandsById: brandLookup,
                typeTitles,
            }),
        [documents, filter, brandId, typeId, query, linkedByNumber, brandLookup, typeTitles],
    );

    const brandOptions = useMemo(
        () =>
            sortedOptions(
                documents.flatMap((doc) => doc.brandIds),
                (id: BrandId) => brandLabel(brandLookup.get(id), id),
            ),
        [documents, brandLookup],
    );
    const typeOptions = useMemo(
        () =>
            sortedOptions(
                documents.flatMap((doc) => doc.typeIds),
                (id: EquipmentTypeId) => typeTitles.get(id) ?? id,
            ),
        [documents, typeTitles],
    );
    const columns = useMemo(() => documentColumns(typeTitles), [typeTitles]);

    return (
        <PageShell>
            <PageHeader
                title="Documents"
                description="Every confirmed product document, shared across the models that link it."
            >
                <Input
                    placeholder="Search title, brand, type, model number…"
                    value={searchInput}
                    onChange={(event) => handleSearchChange(event.target.value)}
                    className="w-[260px]"
                />
                <FilterSelect
                    value={brandId}
                    allLabel="All brands"
                    options={brandOptions}
                    onChange={(value) => setSearchParam('brand', value)}
                />
                <FilterSelect
                    value={typeId}
                    allLabel="All types"
                    options={typeOptions}
                    onChange={(value) => setSearchParam('type', value)}
                />
                <FilterSelect
                    value={filter === 'all' ? undefined : filter}
                    allLabel={DOCUMENT_FILTER_LABELS.all}
                    options={DOCUMENT_FILTERS.filter((value) => value !== 'all').map((value) => ({
                        value,
                        label: DOCUMENT_FILTER_LABELS[value],
                    }))}
                    onChange={(value) => setSearchParam('filter', value)}
                />
            </PageHeader>
            <Card>
                <CardHeader>
                    <CardTitle>Library</CardTitle>
                    <CardDescription>
                        {visible.length} of {documents.length} document{documents.length === 1 ? '' : 's'}
                        {documents.length >= DOCUMENT_LIBRARY_LIMIT ? ` (newest ${DOCUMENT_LIBRARY_LIMIT} loaded)` : ''}
                        . Open a row to see the document and the models that link it.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {visible.length === 0 ? (
                        <div className="section-description">No documents match these filters.</div>
                    ) : (
                        <DataTable
                            columns={columns}
                            rows={visible}
                            rowKey={(doc) => doc.id}
                            rowHref={(doc) => `/products/documents/${encodeURIComponent(doc.id)}`}
                            sort={sort}
                            onSortChange={setSort}
                        />
                    )}
                </CardContent>
            </Card>
        </PageShell>
    );
};

const DOCUMENT_FILTER_LABELS: Record<DocumentFilter, string> = {
    all: 'All documents',
    coverage: 'Has coverage rule',
    'app-visible': 'Shown in the app',
    'library-only': 'Library only',
    'ingest-failed': 'Ingest failed',
};

const ALL = 'all';

/** One filter dropdown: `undefined` is its "all" choice, so the URL stays clean until someone filters. */
const FilterSelect: FC<{
    value: string | undefined;
    allLabel: string;
    options: { value: string; label: string }[];
    onChange: (value: string | undefined) => void;
}> = ({ value, allLabel, options, onChange }) => (
    <Select value={value ?? ALL} onValueChange={(next) => onChange(next === ALL ? undefined : next)}>
        <SelectTrigger className="w-[170px]">
            <SelectValue />
        </SelectTrigger>
        <SelectContent>
            <SelectItem value={ALL}>{allLabel}</SelectItem>
            {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                    {option.label}
                </SelectItem>
            ))}
        </SelectContent>
    </Select>
);

function documentColumns(typeTitles: Map<EquipmentTypeId, string>): DataTableColumn<ModelDocument>[] {
    return [
        {
            key: 'document',
            header: 'Document',
            className: 'min-w-[240px]',
            sortBy: (doc) => documentTitle(doc),
            cell: (doc) => (
                <div className="space-y-1">
                    <div className="font-medium">{documentTitle(doc)}</div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-mono">{doc.id}</span>
                        <a
                            href={doc.storageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                        >
                            PDF
                            <ExternalLink className="h-3 w-3" />
                        </a>
                    </div>
                </div>
            ),
        },
        {
            key: 'brand',
            header: 'Brand',
            className: 'min-w-[150px]',
            cell: (doc) => (
                <div className="flex flex-wrap items-center gap-1.5">
                    <DocumentBrandChips brandIds={doc.brandIds} />
                </div>
            ),
        },
        {
            key: 'type',
            header: 'Type',
            className: 'min-w-[140px]',
            cell: (doc) => (
                <div className="flex flex-wrap items-center gap-1.5">
                    {doc.typeIds.map((typeId) => (
                        <EquipmentTypeBadge key={typeId} typeId={typeId} title={typeTitles.get(typeId)} />
                    ))}
                </div>
            ),
        },
        {
            key: 'tags',
            header: 'Tags',
            className: 'min-w-[180px]',
            cell: (doc) => (
                <div className="flex flex-wrap items-center gap-1.5">
                    <TagChips tags={doc.tags} />
                    <DocumentStateBadges doc={doc} />
                </div>
            ),
        },
        {
            key: 'coverage',
            header: 'Coverage',
            className: 'min-w-[200px]',
            cell: (doc) =>
                doc.coverage ? (
                    <CoveragePills coverage={doc.coverage} />
                ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                ),
        },
        {
            key: 'updatedAt',
            header: 'Updated',
            className: 'min-w-[120px]',
            cellClassName: 'text-sm text-muted-foreground',
            sortBy: (doc) => doc.updatedAt,
            sortFirst: 'desc',
            cell: (doc) => new Date(doc.updatedAt).toLocaleDateString(),
        },
    ];
}

/** Each id once, as an option, ordered by its label (brand ids are opaque, so their order means nothing). */
function sortedOptions<T extends string>(ids: T[], label: (id: T) => string): { value: T; label: string }[] {
    return [...new Set(ids)]
        .map((id) => ({ value: id, label: label(id) }))
        .sort((a, b) => a.label.localeCompare(b.label));
}
